/**
 * Wildcard subdomain → venue-slug resolver. Front-end half of
 * `*.coffeemates.biz` microsite routing (back-end half: ALB host-route +
 * R53 wildcard ALIAS). Used by App.tsx at boot to swap `/` for the venue
 * microsite. Returns null for apex, www, reserved operational subdomains,
 * localhost, multi-label hosts, and non-apex domains.
 */

/**
 * CONTRACT: mirrors the DNS/operational subset of RESERVED_SLUGS in
 * `backend/utils/slugify.py` — a slug the backend rejects must never
 * resolve to a venue here. Adding an operational subdomain (e.g.
 * `metrics`) requires updating both files in the same commit. (Backend's
 * broader product-path/brand blocks can't reach here — those slugs can't
 * be created.)
 */
const RESERVED = new Set<string>([
  'www', 'api', 'app', 'admin', 'ws', 'mail', 'static', 'cdn',
  'staging', 'dev', 'test', 'preview',
]);

/**
 * Returns the venue slug (lowercase, single label) when host is
 * `<slug>.<apex>` AND non-reserved AND single-label; otherwise null.
 * `host` may include `:port`. `apex` override is for tests only.
 */
export function venueSlugFromHost(
  host: string | null | undefined,
  apex = 'coffeemates.biz',
): string | null {
  if (!host) return null;

  const h = host.toLowerCase().split(':')[0].trim();
  if (!h) return null;

  if (h === apex || h === `www.${apex}`) return null;

  if (!h.endsWith(`.${apex}`)) return null;

  const prefix = h.slice(0, h.length - apex.length - 1);

  // Single-label only: nested labels (a.b.coffeemates.biz) are
  // operator-only and must 404 here.
  if (!prefix || prefix.includes('.')) return null;

  if (RESERVED.has(prefix)) return null;

  return prefix;
}


/**
 * True when host is a BYO custom domain: FQDN-shaped (contains a dot),
 * not localhost/loopback (127.0.0.1 and 0.0.0.0 → false), and not the
 * CoffeeMates apex or any of its subdomains. On true, App.tsx hits
 * `GET /venues/by-domain?hostname=<host>`: 200 → navigate to
 * `/venues/<slug>`; 404 → "Not configured" splash.
 */
export function isCustomDomain(
  host: string | null | undefined,
  apex = 'coffeemates.biz',
): boolean {
  if (!host) return false;
  const h = host.toLowerCase().split(':')[0].trim();
  if (!h) return false;
  if (h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0') return false;
  // Bare label like `mycafe` — not a routable hostname.
  if (!h.includes('.')) return false;
  if (h === apex || h.endsWith(`.${apex}`)) return false;
  return true;
}
